import { ITrainingPlanNavigationState } from "@Components/training-plans/training-plans-drawer/training-plans-drawer.utils";
import { DEFAULT_POSITION_SELECTOR_CONFIG, flattenPositionConfig } from "@Constants/game/position-config";
import getLeagueInfo from "@Containers/shared/helpers/get-league-info";
import { Actions, AppContext, Events, UnityBridge } from "@Context/common/general/context";
import { GameModes, MetricEventTypes, PlayPositions, PlaySessionEventTypes, PlaySessionTypes } from "@Enums";
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import { Division, League } from "@Globals/types";
import { loadPositionsFromStorage, savePositionsToStorage } from "@Helpers/position-storage";
import { useApiMutation, useApiQuery } from "@hooks";
import useCompletePlaySession from "@hooks/game/use-complete-play-session";
import useI18n from "@i18n";
import { Api } from "@Shared/schemas";
import { CreatePlaySessionResponse } from "@Shared/schemas/sessions/playSessionSchemas";
import { PlaySessionEvent } from "@Types/unity-events";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

const defaultCareerPositions = flattenPositionConfig(DEFAULT_POSITION_SELECTOR_CONFIG.positions).map(position => position.id);
const getValidCareerPositions = (positions: PlayPositions[]) => positions.filter(position => defaultCareerPositions.includes(position));
const loadCareerPositionsFromStorage = () => getValidCareerPositions(loadPositionsFromStorage());

const metricEventTypes = [
  MetricEventTypes.SCAN_FREQUENCY,
  MetricEventTypes.SCAN_TIMING,
  MetricEventTypes.CRITICAL_SCAN,
  MetricEventTypes.AWARENESS,
  MetricEventTypes.DECISION,
];

export interface UseCareerResponse {
  showCountdown: boolean;
  starting: boolean;
  showPage: boolean;
  modalMessage: string;
  leagueChange: string | null;
  league: League | null;
  division: Division | null;
  nextLeague: League | null;
  remainingDivisions: number;
  showCareerContent: boolean;
  metricScores: { metric: MetricEventTypes; score: number }[];
  selectedPositions: PlayPositions[];
  onCountdownComplete: () => void;
  onPlayClicked: () => void;
  setModalMessage: (message: string) => void;
  setShowCountdown: (show: boolean) => void;
  setSelectedPositions: (positions: PlayPositions[]) => void;
  isCompletingSession: boolean;
}

export const useCareer = (): UseCareerResponse => {
  const { state, dispatch } = useContext(AppContext);
  const { t } = useTranslation("VR");
  const { i18n } = useI18n();
  const location = useLocation();
  const locationState = (location.state as ITrainingPlanNavigationState | null) ?? null;
  const trainingPlanLaunchConfig =
    locationState?.trainingPlanLaunchConfig?.source === "training-plan" && locationState.trainingPlanLaunchConfig.playMode === "career"
      ? locationState.trainingPlanLaunchConfig
      : null;

  const [selectedPositions, setSelectedPositions] = useState<PlayPositions[]>(loadCareerPositionsFromStorage());
  const [positionsInitialized, setPositionsInitialized] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [starting, setStarting] = useState(false);
  const [showPage, setShowPage] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [leagueChange, setLeagueChange] = useState<string | null>(null);
  const [league, setLeague] = useState<League | null>(null);
  const [division, setDivision] = useState<Division | null>(null);
  const [nextLeague, setNextLeague] = useState<League | null>(null);
  const [remainingDivisions, setRemainingDivisions] = useState(0);
  const [metricScores, setMetricScores] = useState<{ metric: MetricEventTypes; score: number }[]>([]);
  const showCareerContent = !!(division && !starting);
  
  const playSessionRef = useRef<CreatePlaySessionResponse | null>(null);
  const isPreparingSessionRef = useRef(false);

  const createPlaySession = useApiMutation({ api: Api.Sessions.CreatePlaySession });
  const progressionQuery = useApiQuery({
    api: Api.Progression.GetUserProgression,
    request: { user_id: state.user.dbId },
    queryKey: [state.user.dbId],
    enabled: Boolean(state.user.dbId),
    staleTime: 0,
  });

  const showPlaylistResults = (resultsData: { play_session_id?: number }) => {
    Events.app.navigate.emit({
      path: `/home/career/results/${resultsData.play_session_id}`,
      data: resultsData,
    });
  };

  const onDataResolved = (progression: NonNullable<typeof progressionQuery.data>) => {
    dispatch({
      type: Actions.CoreActions.UpdateState,
      payload: {
        user: {
          userRating: progression.user_rating,
        },
      },
    });

    const { priorLeague, league, nextLeague, division, nextDivision, remainingDivisions } = getLeagueInfo(
      progression.progression_config.leagues,
      progression.user_rating,
      progression.previous_user_rating
    );

    let message = "";
    const hasChangedDivision =
      division &&
      nextDivision &&
      (progression.previous_user_rating < division.rating_point ||
        progression.previous_user_rating >= nextDivision.rating_point ||
        progression.user_rating === 0);

    const hasChangedLeague =
      priorLeague &&
      league &&
      ((league.rating_point || 0) > (priorLeague.rating_point || 0) || (league.rating_point || 0) < (priorLeague.rating_point || 0));

    setLeague(league);
    setNextLeague(nextLeague);
    setDivision(division);
    setRemainingDivisions(remainingDivisions);

    if (hasChangedLeague && league) {
      if ((league.rating_point || 0) > (priorLeague?.rating_point || 0)) {
        message = t(`career.message.promotion.${league.name.toLowerCase()}`);
        setLeagueChange(message);
      } else {
        message = t(`career.message.demotion.${league.name.toLowerCase()}`);
        setLeagueChange(message);
      }
    } else {
      if (hasChangedDivision && league && division) {
        setModalMessage(t(`career.message.briefing.${league.name.toLowerCase()}.${division.name}`));
      }
    }

    setShowPage(true);
  };

  const onCountdownComplete = () => {
    const playSession = playSessionRef.current;
    if (!playSession) return;

    setStarting(true);
    setShowCountdown(false);

    UnityBridge.dispatchWebEvent({
      type: UnityBridge.OutgoingWebEvents.StartPlaylist,
      payload: {
        items: playSession.play_items,
        play_session_id: playSession.play_session_id,
      },
    });
  };

  const onPlayClicked = async () => {
    if (isPreparingSessionRef.current || starting || !positionsInitialized) return;
    isPreparingSessionRef.current = true;
    try {
      playSessionRef.current = await createPlaySession.mutateAsync({
        account_id: state.account?.id ?? null,
        game_mode: GameModes.SCENARIO,
        session_type: PlaySessionTypes.PROGRESSION_SESSION,
        progression_session_config: { play_positions: selectedPositions },
      });
      setShowCountdown(true);
    } catch (error) {
      console.error("Failed to prepare Career session:", error);
    } finally {
      isPreparingSessionRef.current = false;
    }
  };

  const onCareerEventReceived = ({ type, data }: PlaySessionEvent) => {
    if (type === PlaySessionEventTypes.CHALLENGE_ACCEPTED) {
      setModalMessage(t(`career.message.briefing.${data?.league}.${data?.division}`));
    }
  };

  const { isCompletingSession } = useCompletePlaySession({
    onSuccess: (results, playSessionId) => showPlaylistResults({ ...results, play_session_id: playSessionId }),
  });

  const onGameplayExited = () => {
    setStarting(false);
    playSessionRef.current = null;
    void progressionQuery.refetch();
  };

  // Initialize positions from storage
  useEffect(() => {
    const storedPositions = loadPositionsFromStorage();
    const validPositions = getValidCareerPositions(storedPositions);
    if (storedPositions.length !== validPositions.length) {
      savePositionsToStorage(validPositions);
    }
    const initialPositions = trainingPlanLaunchConfig?.playPosition ? [trainingPlanLaunchConfig.playPosition] : validPositions || [];
    setSelectedPositions(initialPositions);
    setPositionsInitialized(true);
  }, [trainingPlanLaunchConfig?.playPosition, location.key]);

  useEffect(() => {
    if (progressionQuery.data) onDataResolved(progressionQuery.data);
  }, [progressionQuery.data]);

  useEffect(() => {
    if (division?.score_config) {
      const targets = division.score_config;
      const metrisWithScores = metricEventTypes
        .map(metric => ({
          metric,
          score: targets?.[metric]?.target_score,
        }))
        .filter(ms => ms.score !== undefined);

      setMetricScores(metrisWithScores);
    }
  }, [division]);

  useEffect(() => {
    state.app.events.showPlaylistResults.uniq(showPlaylistResults);
    state.app.events.gameplayExited.uniq(onGameplayExited);
    state.app.events.playSessionEvent.uniq(onCareerEventReceived);
    Events.app.showHeaderButton.emit({ type: VRHeaderButtonType.BACK, title: i18n("career.label") });

    return () => {
      state.app.events.showPlaylistResults.remove(showPlaylistResults);
      state.app.events.gameplayExited.remove(onGameplayExited);
      state.app.events.playSessionEvent.remove(onCareerEventReceived);
      Events.app.hideHeaderButton.emit();
    };
  }, []);

  const handleSetSelectedPositions = (positions: PlayPositions[]) => {
    setSelectedPositions(positions);
    savePositionsToStorage(positions);
  };

  return {
    showCountdown,
    starting: starting || createPlaySession.isPending,
    showPage,
    modalMessage,
    leagueChange,
    league,
    division,
    nextLeague,
    remainingDivisions,
    showCareerContent,
    metricScores,
    onCountdownComplete,
    onPlayClicked,
    setModalMessage,
    setShowCountdown,
    selectedPositions,
    setSelectedPositions: handleSetSelectedPositions,
    isCompletingSession,
  };
};
